/*
 * Copyright (c) Dito Technologies LLC
 */

import React from "react";
import Body from "ui/kit/typography/Body";
import {createFragmentContainer} from "react-relay";
import {graphql} from "babel-plugin-relay/macro";
import {
    PieceRendererNoteChangeDuration_note$data
} from "ui/feature/piece-play/common/__generated__/PieceRendererNoteChangeDuration_note.graphql";
import styles from "ui/feature/piece-play/common/piece-renderer/PieceRendererNoteChangeDuration.module.scss";
import Header2 from "ui/kit/typography/Header2";
import UpdatePieceFileChangeNoteDuration from "mutation/UpdatePieceFileChangeNoteDuration";
import {useDebounce} from "react-use";

interface Props {
    note: PieceRendererNoteChangeDuration_note$data
    pieceId: string;
}

const intToFraction: { [key: number]: string } = {
    1: "1/32",
    2: "1/16",
    3: "3/32",
    4: "1/8",
    5: "5/32",
    6: "3/16",
    7: "7/32",
    8: "1/4",
    9: "9/32",
    10: "5/16",
    11: "11/32",
    12: "3/8",
    13: "13/32",
    14: "7/16",
    15: "15/32",
    16: "1/2",
    17: "17/32",
    18: "9/16",
    19: "19/32",
    20: "5/8",
    21: "21/32",
    22: "11/16",
    23: "23/32",
    24: "3/4",
    25: "25/32",
    26: "13/16",
    27: "27/32",
    28: "7/8",
    29: "29/32",
    30: "15/16",
    31: "31/32",
    32: "1"
}

const PieceRendererNoteChangeDuration: React.FC<Props> = ({note, pieceId}) => {

    const [currentRangeValue, setCurrentRangeValue] = React.useState<number>(Math.round(note.note_duration * 32));

    const onChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentRangeValue(parseInt(e.target.value));
    }, [setCurrentRangeValue]);

    const durationText = React.useMemo(() => {
        const fractionValue: string = intToFraction[currentRangeValue % 32];
        const wholeNumber = Math.floor(currentRangeValue / 32);
        if (currentRangeValue % 32 === 0) {
            return `${wholeNumber}`
        } else if (wholeNumber > 0) {
            return `${wholeNumber} and ${fractionValue}`
        } else {
            return fractionValue;
        }
    }, [currentRangeValue]);

    const executeSave = React.useCallback(async () => {
        const savingValue = currentRangeValue / 32;
        console.log("executing the save of the duration", savingValue);
        if (savingValue === note.note_duration) return;
        await UpdatePieceFileChangeNoteDuration(pieceId, note.bulb_id, savingValue);
    }, [currentRangeValue, note.note_duration, note.bulb_id, pieceId]);

    useDebounce(async () => {
        await executeSave();
    }, 1000, [executeSave]);

    return (
        <div>
            <Header2>Change Duration</Header2>
            <Body>{durationText}</Body>
            <input
                className={styles.range}
                type={"range"}
                onChange={onChange}
                value={currentRangeValue}
                min={1}
                max={64}
            />
        </div>
    );
}

export default createFragmentContainer(PieceRendererNoteChangeDuration, {
    note: graphql`
    fragment PieceRendererNoteChangeDuration_note on MusicPieceFileNote {
        bulb_id
        note_duration
    }
    `
});
